import { Controller } from "@hotwired/stimulus"

// Profile recommendation picker. Searches Atlas's place_recommendations/search
// endpoint and the Google Places API (new: AutocompleteSuggestion + Place) in
// parallel. Each selection POSTs immediately to create the recommendation and
// prepends the returned card into the recommendations grid.
//
// For Google picks we read the place's first photo client-side: render its
// getURI() image into the just-added card for an instant preview (display only,
// never persisted — it carries the browser key), and send the photo *reference*
// (photo.name) + attribution to the server, which resolves and caches a durable
// key-free image URL.
export default class extends Controller {
  static targets = [
    "input", "results", "manualForm",
    "manualTitle", "manualAddress", "manualCity", "manualState", "manualCountry",
    "error",
  ]
  static values = {
    searchUrl: String,
    lookupUrl: String,
    createUrl: String,
    gridSelector: String,
    emptySelector: String,
  }

  connect() {
    this.attachedPlaceIds = new Set()
    this.attachedGoogleIds = new Set()
    this.boundCloseResults = (e) => {
      if (!this.element.contains(e.target)) this.closeResults()
    }
    document.addEventListener("click", this.boundCloseResults)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseResults)
  }

  // Loads the new Places library and seeds a session token. Polls because the
  // maps bootstrap (loading=async) defines importLibrary slightly after load.
  async ensureGoogle() {
    if (this.placesReady) return
    for (let i = 0; i < 50 && !window.google?.maps?.importLibrary; i++) {
      await new Promise((r) => setTimeout(r, 200))
    }
    if (!window.google?.maps?.importLibrary) return
    await google.maps.importLibrary("places")
    if (!this.sessionToken) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken()
    }
    this.placesReady = true
  }

  // === Search ===

  search() {
    clearTimeout(this.debounceTimer)
    const query = this.inputTarget.value.trim()
    if (query.length < 3) {
      this.closeResults()
      return
    }
    this.debounceTimer = setTimeout(() => this.runSearch(query), 300)
  }

  async runSearch(query) {
    this.currentQuery = query
    let atlas = []
    let google = []
    try {
      ;[atlas, google] = await Promise.all([this.fetchAtlas(query), this.fetchGoogle(query)])
    } catch (e) {
      console.error("recommendation-picker search failed", e)
    }
    if (this.currentQuery !== query) return
    this.renderResults(atlas, google)
  }

  async fetchAtlas(query) {
    try {
      const res = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`)
      const data = await res.json()
      return data.matches || []
    } catch {
      return []
    }
  }

  async fetchGoogle(query) {
    try {
      await this.ensureGoogle()
      if (!google.maps?.places?.AutocompleteSuggestion) return []
      const { suggestions } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken: this.sessionToken,
          // Restrict to actual POIs (establishments + natural features like
          // mountains, lakes, waterfalls); excludes cities, states, countries,
          // postal codes, and plain street addresses.
          includedPrimaryTypes: ["establishment", "natural_feature"],
        })
      return (suggestions || []).map((s) => s.placePrediction).filter(Boolean)
    } catch (e) {
      console.error("recommendation-picker google search failed", e)
      return []
    }
  }

  // === Render ===

  renderResults(atlasMatches, googlePredictions) {
    const query = this.currentQuery || this.inputTarget.value.trim()
    const atlasGoogleIds = new Set(atlasMatches.map((m) => m.google_place_id).filter(Boolean))

    const atlasItems = atlasMatches
      .filter((m) => !this.attachedPlaceIds.has(String(m.place_id)))
      .map((m) => ({
        kind: "atlas",
        title: m.title,
        subtitle: m.location || "",
        score: this.matchScore(m.title, query, "atlas"),
        data: m,
      }))

    const googleItems = googlePredictions
      .filter((p) => !atlasGoogleIds.has(p.placeId) && !this.attachedGoogleIds.has(p.placeId))
      .map((p) => {
        const title = p.mainText?.text || p.text?.text || ""
        return {
          kind: "google",
          title,
          subtitle: p.secondaryText?.text || "",
          score: this.matchScore(title, query, "google"),
          placeId: p.placeId,
        }
      })

    const merged = [...atlasItems, ...googleItems].sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      if (a.kind === b.kind) return 0
      return a.kind === "atlas" ? -1 : 1
    })

    let html = merged
      .map((item) => {
        if (item.kind === "atlas") {
          return `
            <button type="button"
                    class="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer flex items-start gap-2"
                    data-action="click->recommendation-picker#selectAtlas"
                    data-place-id="${this.escapeAttr(String(item.data.place_id))}"
                    data-google-place-id="${this.escapeAttr(item.data.google_place_id || "")}"
                    data-title="${this.escapeAttr(item.title)}">
              <span class="mt-0.5 inline-flex shrink-0 items-center justify-center w-5 h-5 rounded-full bg-cyan-100 text-cyan-800 text-[9px] font-bold" title="Atlas Obscura page">AO</span>
              <span class="flex-1 min-w-0">
                <span class="block font-medium text-gray-900 truncate">${this.escapeHtml(item.title)}</span>
                ${item.subtitle ? `<span class="block text-xs text-gray-500 truncate">${this.escapeHtml(item.subtitle)}</span>` : ""}
              </span>
            </button>`
        }
        return `
          <button type="button"
                  class="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer flex items-start gap-2"
                  data-action="click->recommendation-picker#selectGoogle"
                  data-google-place-id="${this.escapeAttr(item.placeId)}">
            <span class="mt-0.5 inline-block shrink-0 w-5 h-5"></span>
            <span class="flex-1 min-w-0">
              <span class="block font-medium text-gray-900 truncate">${this.escapeHtml(item.title)}</span>
              <span class="block text-xs text-gray-500 truncate">${this.escapeHtml(item.subtitle)}</span>
            </span>
          </button>`
      })
      .join("")

    if (!html) {
      html = `<div class="px-3 py-2 text-sm text-gray-500">No matches.</div>`
    }

    html += `
      <button type="button"
              class="w-full text-left px-3 py-2 text-sm border-t border-gray-200 hover:bg-gray-50 cursor-pointer text-indigo-600"
              data-action="click->recommendation-picker#showManual">
        Can't find it? Add manually
      </button>`

    this.resultsTarget.innerHTML = html
    this.resultsTarget.classList.remove("hidden")
  }

  matchScore(text, query, kind = null) {
    if (!text || !query) return 0
    const t = text.toLowerCase()
    const q = query.toLowerCase()
    if (t === q) return 100
    if (t.startsWith(q)) return kind === "atlas" ? 100 : 80
    if (t.includes(q)) return 60
    if (q.startsWith(t)) return 50
    const tWords = t.split(/\s+/).filter(Boolean)
    const qWords = q.split(/\s+/).filter(Boolean)
    if (qWords.length === 0) return 0
    const hits = qWords.filter((qw) => tWords.some((tw) => tw.startsWith(qw))).length
    return Math.round((hits / qWords.length) * 40)
  }

  closeResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  // === Selection handlers (POST immediately) ===

  selectAtlas(event) {
    const placeId = event.currentTarget.dataset.placeId
    const googlePlaceId = event.currentTarget.dataset.googlePlaceId || ""
    if (this.attachedPlaceIds.has(String(placeId))) return
    this.closeResults()
    this.inputTarget.value = ""
    this.createRecommendation({ place_id: placeId }, { placeId, googlePlaceId })
    if (googlePlaceId) this.resetSession()
  }

  selectGoogle(event) {
    const googlePlaceId = event.currentTarget.dataset.googlePlaceId
    this.closeResults()
    this.inputTarget.value = ""

    fetch(`${this.lookupUrlValue}?google_place_id=${encodeURIComponent(googlePlaceId)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.place_id) {
          if (this.attachedPlaceIds.has(String(data.place_id))) return
          this.createRecommendation({ place_id: data.place_id }, { placeId: data.place_id, googlePlaceId })
          this.resetSession()
        } else {
          this.fetchGoogleDetailsAndCreate(googlePlaceId)
        }
      })
      .catch(() => this.fetchGoogleDetailsAndCreate(googlePlaceId))
  }

  async fetchGoogleDetailsAndCreate(googlePlaceId) {
    try {
      const place = new google.maps.places.Place({ id: googlePlaceId })
      await place.fetchFields({
        fields: ["id", "displayName", "formattedAddress", "addressComponents", "location", "photos", "types"],
      })
      const addr = this.parseAddressComponents(place.addressComponents)
      const photo = place.photos?.[0]
      const imageUrl = photo ? photo.getURI({ maxWidth: 800 }) : null
      const reference = photo?.name || null
      const attribution = photo?.authorAttributions?.[0]?.displayName || null

      this.createRecommendation(
        {
          community_place: {
            title: place.displayName,
            google_place_id: place.id,
            address1: addr.address1,
            address2: addr.address2,
            city: addr.city,
            state: addr.state,
            country: addr.country,
            postal_code: addr.postal_code,
            latitude: place.location?.lat(),
            longitude: place.location?.lng(),
            cached_google_image_reference: reference,
            cached_google_image_attribution: attribution,
          },
        },
        { googlePlaceId: place.id, imageUrl, attribution }
      )
      this.resetSession()
    } catch (e) {
      console.error("recommendation-picker place details failed", e)
      this.showError("Could not load that place. Try again.")
    }
  }

  resetSession() {
    if (google.maps?.places?.AutocompleteSessionToken) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken()
    }
  }

  // === Manual entry ===

  showManual() {
    this.closeResults()
    this.manualFormTarget.classList.remove("hidden")
    this.manualTitleTarget.focus()
  }

  hideManual() {
    this.manualFormTarget.classList.add("hidden")
    this.resetManualForm()
  }

  addManual() {
    const title = this.manualTitleTarget.value.trim()
    if (title.length === 0) {
      this.manualTitleTarget.focus()
      return
    }
    this.createRecommendation(
      {
        community_place: {
          title,
          address1: this.manualAddressTarget?.value.trim() || "",
          city: this.manualCityTarget?.value.trim() || "",
          state: this.manualStateTarget?.value.trim() || "",
          country: this.manualCountryTarget?.value.trim() || "",
        },
      },
      {}
    )
    this.hideManual()
  }

  resetManualForm() {
    this.manualTitleTarget.value = ""
    if (this.hasManualAddressTarget) this.manualAddressTarget.value = ""
    if (this.hasManualCityTarget) this.manualCityTarget.value = ""
    if (this.hasManualStateTarget) this.manualStateTarget.value = ""
    if (this.hasManualCountryTarget) this.manualCountryTarget.value = ""
  }

  // === Persistence ===

  async createRecommendation(payload, dedupe) {
    this.clearError()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        this.showError(data.error || "Could not save that place. Try again.")
        return
      }
      const data = await res.json()
      if (dedupe.placeId) this.attachedPlaceIds.add(String(dedupe.placeId))
      if (dedupe.googlePlaceId) this.attachedGoogleIds.add(dedupe.googlePlaceId)
      this.prependCard(data.html, data.id, dedupe.imageUrl, dedupe.attribution)
    } catch (e) {
      this.showError("Could not save that place. Try again.")
    }
  }

  // The create endpoint is idempotent (find_or_create_by), so re-adding a place
  // already shown on the page returns the existing recommendation. Skip the
  // prepend in that case so we don't render a duplicate card for one rec.
  prependCard(html, recommendationId, imageUrl, attribution) {
    const grid = document.querySelector(this.gridSelectorValue)
    if (!grid) return
    if (recommendationId != null && grid.querySelector(`[data-recommendation-id="${recommendationId}"]`)) {
      return
    }
    const empty = this.emptySelectorValue && document.querySelector(this.emptySelectorValue)
    if (empty) empty.remove()
    grid.insertAdjacentHTML("afterbegin", html)
    if (imageUrl) this.swapInstantImage(grid.firstElementChild, imageUrl, attribution)
  }

  // Replace the server-rendered placeholder with the Google photo immediately,
  // so the person adding a place sees it without a reload. Display only — the
  // durable cached URL is resolved + stored server-side from the photo reference.
  swapInstantImage(cardEl, imageUrl, attribution) {
    const placeholder = cardEl?.querySelector(".aon-card-image-placeholder")
    if (!placeholder) return
    const wrap = placeholder.parentElement
    const img = document.createElement("img")
    img.src = imageUrl
    img.loading = "lazy"
    img.className = "aspect-[3/2] w-full object-cover"
    placeholder.replaceWith(img)
    if (attribution && wrap) {
      wrap.classList.add("relative")
      const span = document.createElement("span")
      span.className =
        "absolute bottom-0 right-0 bg-black/40 px-1 py-0.5 text-[10px] leading-none text-white"
      span.textContent = `Photo: ${attribution}`
      wrap.appendChild(span)
    }
  }

  showError(message) {
    if (this.hasErrorTarget) this.errorTarget.textContent = message
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.textContent = ""
  }

  // === Helpers ===

  parseAddressComponents(components) {
    const get = (type) => components?.find((c) => c.types?.includes(type))
    const streetNumber = get("street_number")?.longText || ""
    const route = get("route")?.longText || ""
    const address1 = [streetNumber, route].filter(Boolean).join(" ")
    const subpremise = get("subpremise")?.longText || ""
    const address2 = subpremise ? `#${subpremise}` : ""
    return {
      address1,
      address2,
      city: get("locality")?.longText || get("sublocality")?.longText || "",
      state: get("administrative_area_level_1")?.longText || "",
      country: get("country")?.longText || "",
      postal_code: get("postal_code")?.shortText || "",
    }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML
  }

  escapeAttr(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
